/**
 * @file fileImportProvider.ts
 * Extension point for host environments that replace the standard file
 * open/drop flow of <as-file-upload> with their own native dialogs and file
 * conversion (plugin pattern — the component never imports host-specific
 * code; plugins self-register here, keyed by appEnv, e.g. grabcadPlugin.ts).
 */

/** A file produced by a provider, ready for app consumption. */
export interface ImportedFile {
  filename: string;
  filetype: string;
  data: ArrayBuffer | Blob;
}

/** Per-interaction context handed to provider calls. */
export interface FileImportContext {
  /** Read a provider-specific option attribute off the component (e.g.
   *  grabcad-stl-unit, multiple). */
  getAttribute(name: string): string | null;
  /** Called with conversion progress where the provider reports it — a number
   *  or a stage string, depending on the host. */
  onProgress?: (progress: string | number) => void;
}

export interface FileImportProvider {
  /** Content kinds this provider claims (e.g. ['mesh']). The component routes an
   *  upload through the provider only when its category is listed here;
   *  uploads of any other kind fall back to the native file input. Omit to claim
   *  every kind (the legacy behaviour). Distinct from the export-schema
   *  FileCategory — this is a coarse import-routing class, not a file role. */
  kinds?: readonly string[];
  /** Replaces the native file picker. Resolves the imported file(s) — several
   *  only when the component allows it (`multiple` attribute, readable off the
   *  context) — or null when the dialog is dismissed or the conversion is
   *  aborted. */
  pickFile(ctx: FileImportContext): Promise<ImportedFile[] | null>;
  /** Synchronously decide whether to claim dropped files (e.g. to convert
   *  them). Return the flow to run, or null to let the normal drop flow
   *  proceed. The flow is invoked only once the component is free to run it. */
  claimDrop(blobs: Blob[], ctx: FileImportContext): (() => Promise<ImportedFile[] | null>) | null;
}

const providers = new Map<string, FileImportProvider>();

/** Register a provider for an appEnv value (e.g. 'grabcad'). */
export function registerFileImportProvider(env: string, provider: FileImportProvider): void {
  providers.set(env, provider);
}

export function getFileImportProvider(env: string | undefined | null): FileImportProvider | null {
  return (env && providers.get(env)) || null;
}

/**
 * Like getFileImportProvider, but only returns the provider when it claims the
 * given content kind. A provider with no declared `kinds` claims all kinds.
 * Used by <as-file-upload> so that, e.g., a mesh-only host provider (GrabCAD)
 * handles mesh uploads while image/text/data uploads fall through to the native
 * file input.
 */
export function getFileImportProviderForKind(
  env: string | undefined | null,
  kind: string,
): FileImportProvider | null {
  const provider = getFileImportProvider(env);
  if (!provider) return null;
  if (provider.kinds && !provider.kinds.includes(kind)) return null;
  return provider;
}
